/**
 * Interface for the Article Schema structure according to schema.org
 */
interface ArticleSchema {
  '@context': string;
  '@type': string;
  headline?: string;
  image?: string;
  datePublished?: string;
  author: Array<{
    '@type': string;
    name?: string;
    url?: string;
  }>;
}

/**
 * Creates a schema.org Article metadata object for SEO
 *
 * @param params - Parameters needed to create the article schema
 * @param params.title - The article headline/title
 * @param params.imageUrl - URL of the article's featured image
 * @param params.publishDate - ISO date string when the article was published
 * @param params.organizationName - Name of the publishing organization
 * @param params.organizationUrl - URL of the publishing organization
 * @returns A properly formatted schema.org Article object
 */
export const createArticleSchema = ({
  title,
  imageUrl,
  publishDate,
  organizationName,
  organizationUrl,
}: {
  title?: string;
  imageUrl?: string;
  publishDate?: string;
  organizationName?: string;
  organizationUrl?: string;
}): ArticleSchema => {
  return {
    '@context': 'http://schema.org/',
    '@type': 'Article',
    headline: title,
    image: imageUrl,
    datePublished: publishDate,
    author: [
      {
        '@type': 'Organization',
        name: organizationName,
        url: organizationUrl,
      },
    ],
  };
};

/**
 * Creates a schema.org BreadcrumbList metadata object for SEO
 *
 * @param items - List of breadcrumb items
 * @param items.name - The name of the breadcrumb item
 * @param items.url - The URL of the breadcrumb item
 * @returns A properly formatted schema.org BreadcrumbList object
 */
export const createBreadcrumbSchema = (
  items: { name: string; url: string }[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export type ProficiencyLevel = 'Beginner' | 'Intermediate' | 'Expert';

export const createTechArticleSchema = ({
  title,
  description,
  authorName,
  authorUrl,
  publishDate,
  proficiencyLevel,
  dependencies,
  pageUrl,
}: {
  title: string;
  description: string;
  authorName: string;
  authorUrl: string;
  publishDate: string;
  proficiencyLevel?: string;
  dependencies?: string;
  pageUrl: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: title,
  description,
  author: [
    {
      '@type': 'Person',
      name: authorName,
      url: authorUrl,
    },
  ],
  datePublished: publishDate,
  ...(proficiencyLevel && { proficiencyLevel }),
  ...(dependencies && { dependencies }),
  url: pageUrl,
});
